import {
  useDeleteComment,
  useEditComment,
  useFetchShiftComments,
  mapShiftEventCommentsToCommonComment
} from '@domain/shifthandover'
import {Comments} from '@hconnect/common/comments'
import {DataContentWrapper} from '@hconnect/common/containers'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Box, SxProps, Theme, Typography} from '@mui/material'
import {Moment} from 'moment-timezone'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'

type Props = {
  plantId: string
  cardBoxSx?: SxProps<Theme>
  from?: Moment
  to?: Moment
  placeholderWrapperSx?: SxProps<Theme>
}

export const ShiftCommentsContainer: React.FC<Props> = ({
  plantId,
  cardBoxSx,
  placeholderWrapperSx,
  from,
  to
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const timeZone = useUpmPlantTimezone(plantId)

  const isTimeRangeSupported = !!from && !!to
  const {data, isLoading, isError, isRefetching, refetch} = useFetchShiftComments({
    plantId,
    from: from?.toISOString(),
    to: to?.toISOString(),
    timezone: timeZone,
    options: {
      enabled: isTimeRangeSupported,
      select: (comments) => mapShiftEventCommentsToCommonComment(comments, timeZone)
    }
  })

  const {mutate: deleteShiftComment, isPending: isDeleting} = useDeleteComment({
    plantId,
    timezone: timeZone,
    isShiftComment: true
  })
  const {mutate: updateShiftComment, isPending: isUpdating} = useEditComment({
    plantId,
    timezone: timeZone,
    isShiftComment: true
  })

  return (
    <CardBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...cardBoxSx
      }}
      data-test-id="comments-card"
    >
      <Box display="flex" flexDirection="row">
        <Typography variant="h3" mb={2}>
          {t('comments.cardTitle')}
        </Typography>
      </Box>

      <DataContentWrapper
        showLoader={isLoading}
        isError={isError}
        isRefetching={isRefetching}
        data={data}
        retryFunction={() => void refetch()}
        progressSx={{color: 'primary.main'}}
        placeholderWrapperSx={placeholderWrapperSx}
        noDataSettings={{
          ...(!isTimeRangeSupported && {
            noDataMessage: t('comments.message.unsupportedTimeRange')
          })
        }}
      >
        <Box sx={{overflowY: 'auto'}}>
          <Comments
            data={data}
            isLoading={isLoading}
            showHeader={false}
            showAdd={false}
            deleteCommentProps={{
              onDelete: (id: string) => {
                deleteShiftComment({commentId: id})
              },
              deletingInProgress: isDeleting
            }}
            editCommentProps={{
              onEdit: (commentId: string, commentText: string) => {
                updateShiftComment({commentId: commentId, description: commentText})
              },
              savingChangesInProgress: isUpdating
            }}
            timeZone={timeZone}
            translate={t}
            language={language}
          />
        </Box>
      </DataContentWrapper>
    </CardBox>
  )
}
